import { PrimaryButton } from "components/Button/style";
import { CompositeContainer } from "components/CompositeContainer";
import { DropdownSelect } from "components/DropdownSelect";
import FileUpload from "components/UploadArquivos";
import { Col, Form, Row } from "react-bootstrap";
import { FaDownload } from "react-icons/fa";
import { ButtonsContainer, UploadContainer } from "./style";
import { useEmpresas } from "hooks/useEmpresas";
import { useDivisoes } from "hooks/useDivisoes";
import { useEffect, useMemo, useState } from "react";
import { useDespachantes } from "hooks/useDespachantes";
import { useServicos } from "hooks/useServicos";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { getToken, parseJwt } from "_services/auth";
import { Cookies } from "_utils/Cookies";
import { makeWorker } from "features/processo/_workers";
import Swal from "sweetalert2";
import { cadastrarProcessoLoteSchema } from "features/processo/validator";
import { useCentroCusto } from "hooks/useCentroCusto";
import { Required } from "components/Required";
import { useParceirosDespachante } from "hooks/useParceirosDespachante";

export interface ICadastrarProcessoLote {
  empresa: NullableNumber;
  divisao: NullableNumber;
  despachante: NullableNumber;
  servico: NullableNumber;
  arquivo: any;
  centroCusto: NullableNumber;
  solicitante: NullableNumber;
  parceiro: NullableNumber;
}

export function ImportarExcelProcessos({ setLoadingPage }: any) {
  const { idEmpresa, idDespachante } = parseJwt();

  const [empresaSelecionada, setEmpresaSelecionada] = useState(null);
  const [despachanteSelecionado, setDespachanteSelecionado] = useState(null);
  const { empresasOptions } = useEmpresas();
  const { divisoesOptions } = useDivisoes(empresaSelecionada);
  const { despachantesOptions } = useDespachantes(empresaSelecionada);
  const { servicosOptions } = useServicos(
    empresaSelecionada,
    despachanteSelecionado
  );
  const [centroCustoEscolhido, setCentroCustoEscolhido] = useState(null);
  const { centrosCustoOptions, solicitantesOptions } = useCentroCusto(
    empresaSelecionada,
    centroCustoEscolhido
  );
  const { parceirosOptions } = useParceirosDespachante(despachanteSelecionado);
  const [parceiroSelecionado, setParceiroSelecionado] = useState<number | null>(
    null
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitted },
    reset,
    setValue,
  } = useForm<ICadastrarProcessoLote>({
    resolver: yupResolver<ICadastrarProcessoLote>(cadastrarProcessoLoteSchema),
  });

  useEffect(() => {
    if (empresasOptions.length && idEmpresa) {
      setEmpresaSelecionada(idEmpresa);
      setValue("empresa", idEmpresa);
    }

    if (despachantesOptions.length && idDespachante) {
      setDespachanteSelecionado(idDespachante);
    }
  }, [idEmpresa, idDespachante, empresasOptions, despachantesOptions, reset]);

  const worker = useMemo(makeWorker, []);

  function handleConsultarPlanilha(dados: ICadastrarProcessoLote) {
    Swal.fire(
      "Solicitar Processo em lote",
      "<span>Isso pode demorar um pouco</span>",
      "info"
    )
      .then(() => {
        setLoadingPage(true);
        const baseURL = process.env.REACT_APP_API_DESPACHANTE_BASE_URL;
        const baseURLv2 = process.env.REACT_APP_MS_AW;

        const apiHandler = {
          token: getToken(),
          baseURL,
          baseURLv2,
        };

        worker.postMessage({
          type: "consulta",
          dados,
          apiHandler,
        });
      })
      .catch(() => {});
  }

  function handleBaixarModelo() {
    const link = document.createElement("a");
    link.download = "Modelo_Cadastrar_Processo_Lote.xlsx";
    link.href = "/app/files/Modelo_Cadastrar_Processo_Lote.xlsx";
    link.click();
  }

  return (
    <CompositeContainer.Root>
      <CompositeContainer.Body>
        <Form noValidate onSubmit={handleSubmit(handleConsultarPlanilha)}>
          <Row style={{ height: "90px" }}>
            <Col lg="3">
              <Form.Group>
                <Controller
                  control={control}
                  name="empresa"
                  render={({ field: { onChange, value } }) => (
                    <DropdownSelect
                      searchable
                      onSelect={(value) => {
                        if (value === null) {
                          reset();
                        }
                        setEmpresaSelecionada(value);
                        onChange(value);
                      }}
                      options={empresasOptions}
                      selectedValue={empresaSelecionada}
                      title="Empresa"
                      mensagemErro={
                        isSubmitted ? errors.empresa?.message : null
                      }
                      required
                    />
                  )}
                />
              </Form.Group>
            </Col>
            <Col lg="3">
              <Form.Group>
                <Controller
                  control={control}
                  name="divisao"
                  render={({ field: { onChange, value } }) => (
                    <DropdownSelect
                      searchable
                      onSelect={onChange}
                      options={divisoesOptions}
                      selectedValue={value}
                      title="Divisão"
                      disabled={!empresaSelecionada}
                      mensagemErro={
                        isSubmitted ? errors.divisao?.message : null
                      }
                      required
                    />
                  )}
                />
              </Form.Group>
            </Col>
            <Col lg="3">
              <Form.Group>
                <Controller
                  control={control}
                  name="centroCusto"
                  render={({ field: { onChange, value } }) => (
                    <DropdownSelect
                      searchable
                      onSelect={(value) => {
                        if (value === null) {
                          setValue("solicitante", null);
                        }
                        setCentroCustoEscolhido(value);
                        onChange(value);
                      }}
                      options={centrosCustoOptions}
                      selectedValue={centroCustoEscolhido}
                      title="Centro de Custo"
                      mensagemErro={
                        isSubmitted ? errors.centroCusto?.message : null
                      }
                      disabled={!empresaSelecionada}
                    />
                  )}
                />
              </Form.Group>
            </Col>
            <Col lg="3">
              <Form.Group>
                <Controller
                  control={control}
                  name="solicitante"
                  render={({ field: { onChange, value } }) => (
                    <DropdownSelect
                      searchable
                      onSelect={onChange}
                      options={solicitantesOptions}
                      selectedValue={value}
                      title="Solicitante"
                      disabled={!centroCustoEscolhido}
                      mensagemErro={
                        isSubmitted ? errors.solicitante?.message : null
                      }
                      required={!!centroCustoEscolhido}
                    />
                  )}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row style={{ height: "90px" }}>
            <Col lg="3">
              <Form.Group>
                <Controller
                  control={control}
                  name="despachante"
                  render={({ field: { onChange } }) => (
                    <DropdownSelect
                      searchable
                      onSelect={(value) => {
                        setDespachanteSelecionado(value);
                        onChange(value);
                      }}
                      options={despachantesOptions}
                      selectedValue={despachanteSelecionado}
                      title="Despachante"
                      disabled={!empresaSelecionada}
                      mensagemErro={
                        isSubmitted ? errors.despachante?.message : null
                      }
                      required
                    />
                  )}
                />
              </Form.Group>
            </Col>
            <Col lg="3">
              <Form.Group>
                <Controller
                  control={control}
                  name="parceiro"
                  render={({ field: { onChange } }) => (
                    <DropdownSelect
                      searchable
                      onSelect={(value) => {
                        setParceiroSelecionado(value);
                        onChange(value);
                      }}
                      options={parceirosOptions}
                      selectedValue={parceiroSelecionado}
                      title="Parceiro"
                      disabled={
                        !despachanteSelecionado || !parceirosOptions.length
                      }
                      mensagemErro={
                        isSubmitted ? errors.parceiro?.message : null
                      }
                    />
                  )}
                />
              </Form.Group>
            </Col>
            <Col lg="3">
              <Form.Group>
                <Controller
                  control={control}
                  name="servico"
                  render={({ field: { onChange, value } }) => (
                    <DropdownSelect
                      searchable
                      onSelect={onChange}
                      options={servicosOptions}
                      selectedValue={value}
                      title="Serviço"
                      disabled={!empresaSelecionada || !despachanteSelecionado}
                      mensagemErro={
                        isSubmitted ? errors.servico?.message : null
                      }
                      required
                    />
                  )}
                />
              </Form.Group>
            </Col>
            <Col lg="3">
              <Form.Group>
                <UploadContainer>
                  <span>
                    Arquivo (Excel) <Required />
                  </span>
                  <Controller
                    name="arquivo"
                    control={control}
                    render={({ field: { onChange } }) => (
                      <FileUpload
                        onFileUpload={onChange}
                        mensagemErro={
                          isSubmitted ? (errors.arquivo?.message as any) : null
                        }
                      />
                    )}
                  />
                </UploadContainer>
              </Form.Group>
            </Col>
          </Row>
          <ButtonsContainer>
            <PrimaryButton buttonWidth="none" type="submit">
              Consultar
            </PrimaryButton>
            <PrimaryButton
              buttonWidth="none"
              type="button"
              onClick={handleBaixarModelo}
              variante="primary"
              outline
            >
              Modelo <FaDownload />
            </PrimaryButton>
          </ButtonsContainer>
        </Form>
      </CompositeContainer.Body>
    </CompositeContainer.Root>
  );
}
